import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useDeleteSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useDeleteSubmitterMaster'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import {
  isRetriableDeleteError,
  mapMasterDeleteError,
} from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'

type Props = {
  isOpen: boolean
  row: SubmitterMasterRow
  onClose: () => void
  onError: (info: { message: string; retriable: boolean }) => void
  errorMessage: string | undefined
  /**
   * errorMessage が set された時の retriable 判定 (deleteError.ts の
   * `isRetriableDeleteError` 結果を親から伝搬)。 false / 未指定 の時のみ
   * 削除ボタンを非表示 + キャンセル → 「閉じる」に切替 (BgMaster / BusinessMaster
   * / PlantMaster の DeleteConfirmDialog と同一挙動、 PR #4456 review)。
   */
  errorIsRetriable: boolean | undefined
}

/**
 * 計画提出者マスタ 削除確認ダイアログ。
 * FK 参照ブロック / 楽観ロック conflict / 対象消失などのエラーは onError
 * コールバックで親にエラー内容 (message + retriable) を通知する。
 */
export const DeleteSubmitterMasterDialog = ({
  isOpen,
  row,
  onClose,
  onError,
  errorMessage,
  errorIsRetriable,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useDeleteSubmitterMaster()

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        id: row.id,
        // row.updatedAt は list response の proto Timestamp をそのまま round-trip。
        // JS Date を経由すると µs 精度が落ち、 backend の DB updated_at (= µs 精度)
        // との optimistic-lock 比較が一致せず Aborted で返る。
        expectedUpdatedAt: row.updatedAt,
      })
      onClose()
      // 削除成功の toast (2026-08-28 デザイナー指摘)。
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      onError({
        message: mapMasterDeleteError(
          t,
          err,
          'page:adminOcctoMaster.submitterMaster.serverError',
        ),
        retriable: isRetriableDeleteError(err),
      })
    }
  }

  // 「削除できない状態が確定した」 判定 (DeleteConfirmDialog と同ロジック、
  // AC-MOI-009 UX 補完 + PR #4456 review 対応)。
  const isNonRetriableError =
    errorMessage !== undefined && errorIsRetriable !== true

  return (
    <ConfirmDialog
      body={
        errorMessage ??
        t('page:adminOcctoMaster.submitterMaster.delete.confirmBody', {
          submitterCode: row.submitterCode,
          submitterName: row.submitterName,
        })
      }
      cancelLabel={isNonRetriableError ? t('common:action.close') : undefined}
      confirmLabel={t(
        'page:adminOcctoMaster.submitterMaster.delete.confirmLabel',
      )}
      destructive
      hideConfirm={isNonRetriableError}
      isLoading={isPending}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t('page:adminOcctoMaster.submitterMaster.delete.confirmTitle')}
    />
  )
}
